import { withNamespaces } from "react-i18next";
import { Button, Card, CardBody, CardHeader, Col, Label, Row, Spinner, UncontrolledTooltip } from "reactstrap";
import { useContext } from "react";
import QueryContext from "../../../../contexts/QueryContext";
import { AvCheckbox, AvCheckboxGroup, AvField, AvForm } from "availity-reactstrap-validation";
import { ComplianceQueryChoices } from "src/modules/3rd-party-management/constants";
import { RiInformationLine } from "react-icons/ri";

const QueryDetails = ({
    t,
    onNextButtonClicked,
    onCancelButtonClicked
}) => {
    const {
        queryType,
        isEdit,
        detailQuery,
        queryData, 
        setQueryData,
        thirdparties,
        handleCreateQueryMutation
    } = useContext(QueryContext);

    const handleSubmit = (_e, values) => {
        const thirdpartiesIds = thirdparties?.split(',')?.map((tp) => tp.split('-')?.[0])

        handleCreateQueryMutation.mutate({
            ...values,
            supplier_ids: (thirdpartiesIds && thirdpartiesIds.length > 0) ? thirdpartiesIds : undefined,
            queryId: queryData?.id
        }, {
            onSuccess: ({ data }) => {
                setQueryData((currentState) => {
                    return {
                        ...currentState,
                        ...data
                    }
                });

                onNextButtonClicked();
            }
        });
    }

    const splitedThirdparties = thirdparties.split(','); 

    const allTypesCount = splitedThirdparties.length;

    const companyTypesCount = splitedThirdparties.filter((tp) => tp.split('-')?.[1] === 'company').length;

    const individualTypesCount = splitedThirdparties.filter((tp) => tp.split('-')?.[1] === 'individual').length;

    const thirdpartiesGroupTitle = (companyTypesCount === allTypesCount) ? 'company' : 
        (individualTypesCount === allTypesCount) ? 'individual' : 'mixed';

    return (
        <Card>
            <CardHeader className="bg-white">
                <h5 className="mb-1">
                    {t('Query Details')}
                </h5>
                <p className="text-muted mb-0">
                    {t('Define the query by providing basic information and selecting the entity groups to include.')}
                </p>
            </CardHeader>
            
            <CardBody>
                {isEdit && (detailQuery.isFetching || !queryData?.id) ? (
                    <Row>
                        <Col sm="12" lg="4" className="mb-4">
                            <div className="dt-field dt-skeleton mb-1" style={{ width: '40%' }}></div>
                            <div className="dt-field dt-skeleton" style={{ width: '70%' }}></div>
                        </Col>
                        <Col sm="12" lg="4" className="mb-4">
                            <div className="dt-field dt-skeleton mb-1" style={{ width: '40%' }}></div>
                            <div className="dt-field dt-skeleton" style={{ width: '70%' }}></div>
                        </Col>
                        <Col sm="12" lg="12">
                            <Row>
                                <Col sm="12" lg="6">
                                    <div className="dt-field dt-skeleton mb-1" style={{ width: '30%' }}></div>
                                    <div className="dt-field dt-skeleton" style={{ width: '70%', height: '120px' }}></div>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                ) : (
                    <AvForm
                        className="needs-validation m-2"
                        onValidSubmit={handleSubmit}
                        model={queryData}
                    >
                        <Row>
                            <Col sm='12' md="6" lg="4" className="mb-3">
                                <Label htmlFor="name">
                                    {t('Title')}
                                    <span className="text-danger me-1">*</span>
                                    <RiInformationLine 
                                        id="title-helper-tooltip" 
                                        size="14" 
                                        style={{cursor: 'pointer'}}
                                    />
                                    <UncontrolledTooltip
                                        target="title-helper-tooltip"
                                    >
                                        {t('Provide a descriptive title to help identify this query.')}
                                    </UncontrolledTooltip>
                                </Label>
                                <AvField
                                    id="name"
                                    name="name"
                                    type="text"
                                    errorMessage={t("This field cannot be blank")}
                                    className="form-control"
                                    validate={{
                                        required: { value: true }
                                    }}
                                    placeholder={t('e.g. High-Risk Third Parties')}
                                />
                            </Col>

                            <Col sm='12' md='6' lg="4" className="mb-3">
                                <Label htmlFor="description">
                                    <span className="me-1">{t('Description')}</span>
                                    <RiInformationLine 
                                        id="description-helper-tooltip" 
                                        size="14" 
                                        style={{cursor: 'pointer'}}
                                    />
                                    <UncontrolledTooltip
                                        target="description-helper-tooltip"
                                    >
                                        {t('Describe the purpose or scope of this query.')}
                                    </UncontrolledTooltip>
                                </Label>
                                <AvField
                                    id="description"
                                    name="description"
                                    type="text"
                                    errorMessage={t("This field cannot be blank")}
                                    className="form-control"
                                    validate={{
                                        required: { value: false }
                                    }}
                                    placeholder={t(queryType === 'screening' ? 'e.g. Screen high-risk third parties' : 'e.g. Monitor high-risk third parties')}
                                />
                            </Col>

                            <Col sm='12' className="mb-4">
                                <Label htmlFor="choices">
                                    {t('Select Search Items Groups')}
                                    <span className="text-danger">*</span>
                                </Label>

                                <AvCheckboxGroup
                                    name="choices"
                                    required
                                    errorMessage={t("Please select at least one entity group")}
                                >
                                    {ComplianceQueryChoices.filter((choice) => {
                                        return choice.shouldDisplay ? choice.shouldDisplay({ thirdpartiesGroupTitle }) : true
                                    }).map((choice, index) => {
                                        return (
                                            <AvCheckbox
                                                key={index}
                                                label={choice.label}
                                                value={choice.value}
                                                id={`group-${choice.value}`}
                                            />
                                        )
                                    })}
                                </AvCheckboxGroup>
                            </Col>

                            <Col sm='12' className="d-flex justify-content-between">
                                <Button
                                    color="secondary"
                                    type="button"
                                    outline
                                    onClick={onCancelButtonClicked}
                                    disabled={handleCreateQueryMutation.isLoading}
                                >
                                    {t('Cancel')}
                                </Button>

                                <div>
                                    <Button
                                        color="primary"
                                        type="submit"
                                        disabled={handleCreateQueryMutation.isLoading}
                                    >
                                        {handleCreateQueryMutation.isLoading && (
                                            <Spinner size="sm" color="light" className="me-1" />
                                        )}

                                        {t('Next')}
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </AvForm>
                )}
            </CardBody>
        </Card>
    )
}

export default withNamespaces()(
    QueryDetails
)